import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaSave, FaArrowLeft, FaEdit, FaInfoCircle, FaCalendarAlt, FaTag, FaLock } from "react-icons/fa";
import api from "../api/axios";
import "./DocumentManagement.css";
import { CATEGORIES, DOC_TYPES } from "./docUtils";

export default function DocumentEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "General",
    document_type: "company",
    entity_type: "",
    entity_id: "",
    entity_name: "",
    has_expiry: false,
    expiry_date: "",
    expiry_alert_days: 30,
    tags: "",
    notes: "",
    is_private: false,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/document-management/${id}`);
        const d = res.data;
        setForm({
          title: d.title || "",
          description: d.description || "",
          category: d.category || "General",
          document_type: d.document_type || "company",
          entity_type: d.entity_type || "",
          entity_id: d.entity_id || "",
          entity_name: d.entity_name || "",
          has_expiry: d.has_expiry || false,
          expiry_date: d.expiry_date || "",
          expiry_alert_days: d.expiry_alert_days || 30,
          tags: d.tags || "",
          notes: d.notes || "",
          is_private: d.is_private || false,
        });
      } catch (e) {
        setError("Failed to load document metadata.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError("Title is required."); return; }
    setSubmitting(true);
    setError("");
    try {
      const payload = { ...form };
      if (!payload.has_expiry) payload.expiry_date = null;
      if (payload.entity_id === "") payload.entity_id = null;
      await api.put(`/document-management/${id}`, payload);
      setSuccess("Document metadata updated successfully.");
      setTimeout(() => navigate(`/dashboard/documents/${id}`), 900);
    } catch (e) {
      setError(e.response?.data?.detail || "Update failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="doc-mgmt-container" style={{ textAlign: "center", paddingTop: 80 }}>
      <div className="spinner-border text-primary" />
    </div>
  );

  return (
    <div className="doc-mgmt-container">
      <div className="doc-mgmt-header">
        <div className="doc-header-left">
          <div className="doc-header-icon-box">
            <FaEdit />
          </div>
          <div>
            <h1 className="doc-mgmt-title">Edit Document Record</h1>
            <p className="doc-mgmt-subtitle">
              Modify indexing tags, category classifications, and compliance dates
            </p>
          </div>
        </div>
        <button className="doc-btn-outline" onClick={() => navigate(`/dashboard/documents/${id}`)}>
          <FaArrowLeft />
          <span>Back to Document</span>
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="doc-detail-card" style={{ padding: 0 }}>
        <div style={{ padding: "18px 24px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
          <span className="doc-section-title" style={{ margin: 0 }}>
            <FaInfoCircle /> Record Metadata
          </span>
          <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#64748b" }}>
            To upload a revised or replacement file, use the "New Revision" action in the Document Overview page.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: "24px" }}>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                  Document Title <span className="text-danger">*</span>
                </label>
                <input type="text" className="form-control" name="title" value={form.title} onChange={handleChange} required />
              </div>

              <div className="col-12">
                <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                  Description
                </label>
                <textarea className="form-control" name="description" value={form.description} onChange={handleChange} rows={2} />
              </div>

              <div className="col-md-6">
                <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                  Category
                </label>
                <select className="form-select" name="category" value={form.category} onChange={handleChange}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                  Document Classification
                </label>
                <select className="form-select" name="document_type" value={form.document_type} onChange={handleChange}>
                  {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                  Entity Type
                </label>
                <select className="form-select" name="entity_type" value={form.entity_type} onChange={handleChange}>
                  <option value="">None</option>
                  <option value="employee">Employee</option>
                  <option value="customer">Customer</option>
                  <option value="supplier">Supplier</option>
                  <option value="sale">Sales Order</option>
                  <option value="invoice">Invoice</option>
                  <option value="receipt">Receipt</option>
                  <option value="quotation">Quotation</option>
                  <option value="payment">Payment Voucher</option>
                  <option value="bulk_sale">Bulk Sale</option>
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                  Associated Record ID
                </label>
                <input type="number" className="form-control" name="entity_id" value={form.entity_id} onChange={handleChange} placeholder="e.g., 42" />
              </div>

              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                  Associated Entity Name
                </label>
                <input type="text" className="form-control" name="entity_name" value={form.entity_name} onChange={handleChange} placeholder="Display title" />
              </div>

              {/* Expiry */}
              <div className="col-12">
                <hr style={{ borderColor: "#e2e8f0", margin: "16px 0" }} />
              </div>

              <div className="col-12">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="has_expiry" name="has_expiry" checked={form.has_expiry} onChange={handleChange} />
                  <label className="form-check-label fw-600" htmlFor="has_expiry" style={{ fontSize: "13px" }}>
                    Enable expiration compliance tracking
                  </label>
                </div>
              </div>

              {form.has_expiry && (
                <>
                  <div className="col-md-6">
                    <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                      Expiration Date
                    </label>
                    <input type="date" className="form-control" name="expiry_date" value={form.expiry_date} onChange={handleChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                      Alert Window: {form.expiry_alert_days} days in advance
                    </label>
                    <input type="range" className="form-range" min="7" max="180" step="1" name="expiry_alert_days" value={form.expiry_alert_days} onChange={handleChange} />
                    <div className="d-flex justify-content-between text-muted" style={{ fontSize: "11px" }}>
                      <span>7 days</span>
                      <span>90 days</span>
                      <span>180 days</span>
                    </div>
                  </div>
                </>
              )}

              <div className="col-12">
                <hr style={{ borderColor: "#e2e8f0", margin: "16px 0" }} />
              </div>

              <div className="col-md-6">
                <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                  Metadata Tags (comma-separated)
                </label>
                <input type="text" className="form-control" name="tags" value={form.tags} onChange={handleChange} placeholder="e.g., license, renewal, 2026" />
              </div>

              <div className="col-md-6">
                <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                  Access Level
                </label>
                <div className="form-check form-switch" style={{ marginTop: 6 }}>
                  <input className="form-check-input" type="checkbox" id="is_private" name="is_private" checked={form.is_private} onChange={handleChange} />
                  <label className="form-check-label" htmlFor="is_private" style={{ fontSize: "13px" }}>
                    <FaLock style={{ fontSize: "11px", marginRight: 4, color: "#64748b" }} />
                    Restricted Access
                  </label>
                </div>
              </div>

              <div className="col-12">
                <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                  Administrative Notes
                </label>
                <textarea className="form-control" name="notes" value={form.notes} onChange={handleChange} rows={3} />
              </div>
            </div>

            <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" className="doc-btn-outline" onClick={() => navigate(`/dashboard/documents/${id}`)} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="doc-btn-primary" disabled={submitting} style={{ minWidth: 140 }}>
                {submitting ? (
                  <><span className="spinner-border spinner-border-sm me-2" role="status" />Saving...</>
                ) : (
                  <><FaSave /><span>Save Changes</span></>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
